import ErrorBoundary from "@Components/ErrorBoundary";
import config from "@Config/index";
import useIsApp, { useIsNewNativeApp } from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import React from "react";

type ComponentType<P = any> = React.ComponentType<P>;

function wrapperHOC<P>(
  WrappedComponent: ComponentType<P>,
  {
    componentName,
    showForChina = false,
    showForWhiteLable = true,
    showForMiniProgram = true,
    showForApp = true,
    showOnlyForNewNativeApp = false,
    height,
  }: {
    componentName: string;
    showForChina?: boolean;
    showForWhiteLable?: boolean;
    showForMiniProgram?: boolean;
    height?: string;
    showForApp?: boolean;
    /** New native shells only (`app_shell` = ios_native | android_native). Not RN. */
    showOnlyForNewNativeApp?: boolean;
  },
): ComponentType<P> {
  if (config.IS_WHITELABEL_APP && !showForWhiteLable) {
    return () => null;
  }
  if (config.IS_CHINA_APP && !showForChina) {
    return () => null;
  }

  if (config.IS_CHINA_APP && !showForMiniProgram) {
    const MiniProgramComponent: React.FC<P> = (props: any) => {
      const isMiniProgram = useIsMiniProgram();

      if (isMiniProgram) return <></>;
      return (
        <ErrorBoundary height={height} componentName={componentName}>
          <WrappedComponent {...props} />
        </ErrorBoundary>
      );
    };
    MiniProgramComponent.displayName = componentName;
    return MiniProgramComponent;
  }

  if (!showForApp) {
    const AppComponent: React.FC<P> = (props: any) => {
      const isApp = useIsApp();

      if (isApp) return <></>;

      return (
        <ErrorBoundary height={height} componentName={componentName}>
          <WrappedComponent {...props} />
        </ErrorBoundary>
      );
    };
    AppComponent.displayName = componentName;
    return AppComponent;
  }

  if (showOnlyForNewNativeApp) {
    const NewNativeOnlyComponent: React.FC<P> = (props: any) => {
      const isNewNative = useIsNewNativeApp();

      if (!isNewNative) return <></>;

      return (
        <ErrorBoundary height={height} componentName={componentName}>
          <WrappedComponent {...props} />
        </ErrorBoundary>
      );
    };
    NewNativeOnlyComponent.displayName = componentName;
    return NewNativeOnlyComponent;
  }

  const WrappedComponentWithName: React.FC<P> = (props: any) => (
    <ErrorBoundary height={height} componentName={componentName}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  WrappedComponentWithName.displayName = componentName;

  return WrappedComponentWithName;
}

export default wrapperHOC;
